/**
 * Unified Error Handler with Sentry Integration
 *
 * Bridges the gap between user-facing errors and Sentry tracking.
 * All user-visible errors should go through these functions to ensure
 * they are both displayed to the user AND sent to Sentry for monitoring.
 *
 * Usage:
 *   // For setError() pattern (inline error display)
 *   const message = captureError(err, { component: 'Login', action: 'signup' });
 *   setError(message);
 *
 *   // For toast.error() pattern
 *   showErrorToast('Failed to save', err, { component: 'AddToDeckModal' });
 *
 *   // For alert() pattern
 *   showErrorAlert('Upload failed', err, { component: 'ArticleList', action: 'pdf_upload' });
 */

import { toast } from 'sonner';
import { logger } from './logger';
import { auth$ } from '../store/auth$';

/**
 * Error categories for Sentry filtering and alerting
 */
export const ErrorCategory = {
  AUTH: 'auth',           // Login, signup, password reset, SSO
  PAYMENT: 'payment',     // Subscriptions, purchases, credits
  IMPORT: 'import',       // Article, PDF, EPUB, video import
  AI: 'ai',               // AI chat, RAG, learning features
  TRANSLATION: 'translation',
  AUDIO: 'audio',         // TTS, audio playback
  SHARING: 'sharing',     // Share links, list members
  GENERAL: 'general',     // Default category
};

/**
 * Extract error message from various error types
 */
function getErrorMessage(error, fallbackMessage = 'An error occurred') {
  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.error) return typeof error.error === 'string' ? error.error : error.error.message;
  return fallbackMessage;
}

/**
 * Get current user context for error tracking
 */
function getUserContext() {
  try {
    const user = auth$.user.get();
    if (user) {
      return {
        user_id: user.id,
        user_email: user.email,
        user_name: user.username,
      };
    }
  } catch {
    // Auth store may not be initialized
  }
  return {};
}

/**
 * Build full error context for Sentry
 */
function buildErrorContext(error, customContext = {}) {
  const errorMessage = getErrorMessage(error);

  return {
    // Error details
    error_message: errorMessage,
    error_type: error?.name || error?.constructor?.name || 'Error',
    error_code: error?.code || error?.status || null,

    // User context
    ...getUserContext(),

    // Custom context from caller
    ...customContext,

    // Metadata
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  };
}

/**
 * Core function: Capture error to Sentry and return display message
 *
 * Use this for setError() patterns where you need the message back.
 *
 * @param {Error|string|object} error - The error to capture
 * @param {object} context - Additional context (component, action, category, etc.)
 * @param {string} context.component - Component name where error occurred
 * @param {string} context.action - Action that triggered the error
 * @param {string} context.category - Error category from ErrorCategory
 * @param {string} fallbackMessage - Message to show if error has no message
 * @returns {string} The error message for display
 *
 * @example
 * const message = captureError(err, { component: 'Login', action: 'signup', category: ErrorCategory.AUTH });
 * setError(message);
 */
export function captureError(error, context = {}, fallbackMessage = 'An error occurred') {
  const message = getErrorMessage(error, fallbackMessage);
  const fullContext = buildErrorContext(error, {
    category: ErrorCategory.GENERAL,
    ...context,
  });

  // Send to Sentry via logger
  logger.error(`[${fullContext.category}] ${fullContext.component || 'Unknown'}: ${message}`, fullContext);

  return message;
}

/**
 * Show error toast AND capture to Sentry
 *
 * Use this to replace direct toast.error() calls.
 *
 * @param {string} displayMessage - User-friendly message to show in toast
 * @param {Error|string|object} error - The underlying error (for Sentry context)
 * @param {object} context - Additional context
 *
 * @example
 * showErrorToast('Failed to save card', err, { component: 'AddToDeckModal', category: ErrorCategory.GENERAL });
 */
export function showErrorToast(displayMessage, error = null, context = {}) {
  const errorMessage = error ? getErrorMessage(error) : displayMessage;
  const fullContext = buildErrorContext(error, {
    category: ErrorCategory.GENERAL,
    display_message: displayMessage,
    ...context,
  });

  // Send to Sentry via logger
  logger.error(`[${fullContext.category}] ${fullContext.component || 'Unknown'}: ${errorMessage}`, fullContext);

  // Show toast to user
  toast.error(displayMessage);
}

/**
 * Show alert AND capture to Sentry
 *
 * Use this to replace direct alert() calls for errors.
 *
 * @param {string} displayMessage - User-friendly message to show in alert
 * @param {Error|string|object} error - The underlying error (for Sentry context)
 * @param {object} context - Additional context
 *
 * @example
 * showErrorAlert('Failed to upload PDF', err, { component: 'ArticleList', action: 'pdf_upload', category: ErrorCategory.IMPORT });
 */
export function showErrorAlert(displayMessage, error = null, context = {}) {
  const errorMessage = error ? getErrorMessage(error) : displayMessage;
  const fullContext = buildErrorContext(error, {
    category: ErrorCategory.GENERAL,
    display_message: displayMessage,
    ...context,
  });

  // Send to Sentry via logger
  logger.error(`[${fullContext.category}] ${fullContext.component || 'Unknown'}: ${errorMessage}`, fullContext);

  // Show alert to user
  alert(displayMessage);
}

/**
 * Capture error silently (no user display)
 *
 * Use this for errors that shouldn't interrupt the user but should be tracked.
 *
 * @param {Error|string|object} error - The error to capture
 * @param {object} context - Additional context
 *
 * @example
 * captureErrorSilent(err, { component: 'Analytics', action: 'track_event' });
 */
export function captureErrorSilent(error, context = {}) {
  const message = getErrorMessage(error);
  const fullContext = buildErrorContext(error, {
    category: ErrorCategory.GENERAL,
    silent: true,
    ...context,
  });

  // Send to Sentry via logger (warn level for silent errors)
  logger.warn(`[${fullContext.category}] ${fullContext.component || 'Unknown'}: ${message}`, fullContext);
}

export default {
  captureError,
  showErrorToast,
  showErrorAlert,
  captureErrorSilent,
  ErrorCategory,
};
